import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiRequestTrackerService } from 'app/shared/services/forms/api-request-tracker.service';
import { distinctUntilChanged } from 'rxjs';
import { IconNameDirective } from 'shared/directives';
import { ButtonColor, ButtonShape, ButtonSize, ButtonStyle, ButtonType } from 'shared/enums';

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  imports: [
    NgTemplateOutlet,
    MatIcon,
    NgIf,
    MatButton,
    NgClass,
    MatProgressSpinnerModule,
    MatIconButton,
    IconNameDirective,
  ],
})
export class ButtonComponent implements OnChanges {
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonShape = ButtonShape;

  @Input() public ariaLabel?: string;
  @Input() public class?: string;
  @Input() public color?: ButtonColor = ButtonColor.PRIMARY;
  @Input() public hasTrackingKey?: boolean;
  @Input() public icon?: string;
  @Input() public iconClass?: string;
  @Input() public iconPosition: 'prefix' | 'suffix' = 'prefix';
  @Input() public loading?: boolean;
  @Input() public size: ButtonSize = ButtonSize.MD;
  @Input() public shape?: ButtonShape = ButtonShape.DEFAULT;
  @Input() public style?: ButtonStyle = ButtonStyle.FILLED;
  @Input() public type?: ButtonType = ButtonType.BUTTON;
  private _disabled!: boolean | undefined;

  public get disabled(): boolean {
    return !!this._disabled;
  }

  @Input()
  public set disabled(value: boolean | undefined) {
    this._disabled = value;
    this.element.nativeElement.style.pointerEvents = value ? 'none' : 'auto';
  }

  public buttonClasses = `${this.size}`;
  public element = inject(ElementRef);
  protected readonly apiRequestTracker = inject(ApiRequestTrackerService);

  protected apiRequestTrackerSubscription$ = this.apiRequestTracker.isLoading$
    .pipe(distinctUntilChanged())
    .subscribe((isLoading) => {
      if (this.hasTrackingKey) {
        this.disabled = isLoading;
      }
    });

  constructor() {
    this.setClasses();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['color'] || changes['class'] || changes['size']) {
      this.setClasses();
    }
  }

  private setClasses(): void {
    this.buttonClasses = `${this.color} ${this.class || ''} ${this.size}`;
  }
}

export interface IButtonConfig {
  style?: ButtonStyle;
  shape?: ButtonShape;
  size?: ButtonSize;
  type?: ButtonType;
  color?: string;
  text: string;
  class?: string;
  tooltip?: string;
  icon?: string;
  disabled?: boolean;
  action: () => void;
}
